import { useState, useMemo, Dispatch, SetStateAction } from 'react';
import {
  SpaceBetween,
  Container,
  Select,
  Checkbox,
  CheckboxProps,
  Textarea,
  Input,
  Alert,
} from '@amzn/awsui-components-react';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Button from '@amzn/awsui-components-react/polaris/button';
import { useTranslation } from 'react-i18next';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import {
  ambassadorRegistrationPossibleCountries,
  ambassadorRegistrationPossibleSiteTypes,
} from '../../data/ambassadorRegistrationOptions';
import { useCheckAlias } from '../../hooks/ambassadors/checkAliasQuery';

export interface AmbassadorRegistrationFormValues {
  country: OptionDefinition | null;
  city: OptionDefinition | null;
  siteType: OptionDefinition | null;
  siteCode: OptionDefinition | null;
  businessUnit: OptionDefinition | null;
  siteRepresentation: boolean;
  teamRepresentation: boolean;
  employeeLedGroupRepresentation: boolean;
  howDidYouHear: OptionDefinition | null;
  howDidYouHearSpecification: string;
  myManagerHasApprovedThisRole: boolean;
  sendMeACopy: boolean;
  teamLocation?: OptionDefinition | null;
  teamName?: string | undefined;
  teamSize?: number | undefined;
  teamLeadEmail?: string | undefined;
  ergName?: string[] | undefined;
  ergLocation?: OptionDefinition | null;
  siteLead?: string | undefined;
}

export interface AmbassadorRegistrationFormProps {
  formValues: AmbassadorRegistrationFormValues;
  setFormValues: Dispatch<SetStateAction<AmbassadorRegistrationFormValues>>;
  onSubmit: () => void;
  loading: boolean;
  isUpdateForm?: boolean;
}

export function AmbassadorRegistrationForm({
  formValues,
  setFormValues,
  onSubmit,
  loading,
  isUpdateForm,
}: AmbassadorRegistrationFormProps) {
  const [fieldsEditing, setFieldsEditing] = useState<string[]>([]);
  const [siteLeadAliasExists, setSiteLeadAliasExists] = useState<boolean>(true);

  const { t } = useTranslation(['ambassadors', 'translation']);
  const checkAlias = useCheckAlias(formValues.siteLead);

  const howDidYouHearOptions = [
    { label: t('ambassadorRegistration.ACIWiki'), value: '1' },
    { label: t('ambassadorRegistration.togetherAtAmazon'), value: '2' },
    { label: t('ambassadorRegistration.existingCommunityAmbassadorReferral'), value: '3' },
    { label: t('ambassadorRegistration.internalcommunications'), value: '4' },
    { label: t('ambassadorRegistration.aToZ'), value: '5' },
    { label: t('ambassadorRegistration.leadershipRecommendation'), value: '6' },
    { label: t('ambassadorRegistration.otherPleaseSpecify'), value: '7' },
  ];

  const isValid = useMemo(() => {
    const allSelectsChosen = formValues.siteType?.label;

    // one of the representation boxes is chosen
    // and the vlaues for the chosen representations are filled out
    const representationChosen =
      (formValues.siteRepresentation ||
        formValues.teamRepresentation ||
        formValues.employeeLedGroupRepresentation) &&
      ((formValues.siteRepresentation && formValues.siteLead && siteLeadAliasExists) ||
        !formValues.siteRepresentation) &&
      ((formValues.teamRepresentation &&
        formValues.teamLocation?.label &&
        formValues.teamName &&
        formValues.teamSize) ||
        !formValues.teamRepresentation) &&
      ((formValues.employeeLedGroupRepresentation &&
        formValues.ergName &&
        formValues.ergLocation?.label) ||
        !formValues.employeeLedGroupRepresentation);

    return (
      allSelectsChosen &&
      representationChosen &&
      ((formValues.myManagerHasApprovedThisRole &&
        formValues.howDidYouHear?.label &&
        !isUpdateForm) ||
        isUpdateForm)
    );
  }, [formValues, siteLeadAliasExists, isUpdateForm]);

  const checkSiteLeadExists = async () => {
    const checkAliasResponse = await checkAlias.refetch();
    const aliasExists = checkAliasResponse?.data?.length;

    if (aliasExists) {
      return true;
    } else {
      return false;
    }
  };

  const validateSiteLeadAlias = async () => {
    const aliasExists = await checkSiteLeadExists();

    if (aliasExists) {
      setSiteLeadAliasExists(true);
    } else {
      setSiteLeadAliasExists(false);
    }
  };

  const submitForm = async () => {
    const aliasExists = await checkSiteLeadExists();

    if (aliasExists) {
      onSubmit();
    } else {
      setSiteLeadAliasExists(false);
    }
  };

  return (
    <>
      <SpaceBetween direction="vertical" size="m">
        {formValues.siteRepresentation && !siteLeadAliasExists && (
          <Alert
            statusIconAriaLabel="Error"
            type="error"
            header={t('ambassadorRegistration.siteLeadDoesNotExistTitle')}
          >
            {t('ambassadorRegistration.siteLeadDoesNotExist')}
          </Alert>
        )}
        <Container>
          <SpaceBetween direction="vertical" size="m">
            <h2>{t('ambassadorRegistration.workDetails')}</h2>
            <FormField
              stretch
              label={t('ambassadorRegistration.siteType')}
              info={
                <Button
                  onClick={() =>
                    setFieldsEditing((currentlyEditing) => [...currentlyEditing, 'siteType'])
                  }
                  variant="inline-link"
                >
                  {t('ambassadorRegistration.edit')}
                </Button>
              }
            >
              <Select
                selectedOption={formValues.siteType}
                disabled={!fieldsEditing.includes('siteType')}
                onChange={({ detail }) =>
                  setFormValues((fv) => ({ ...fv, siteType: detail.selectedOption }))
                }
                options={ambassadorRegistrationPossibleSiteTypes}
                placeholder={t('ambassadorRegistration.selectOption')}
              />
            </FormField>

            <SpaceBetween direction="vertical" size="xxxs">
              <h2 style={{ marginBottom: 0 }}>{t('ambassadorRegistration.representation')}</h2>
              <span>{t('ambassadorRegistration.moreThanOne')}</span>
            </SpaceBetween>

            <RepresentationCheckbox
              checked={formValues.siteRepresentation}
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, siteRepresentation: detail.checked }))
              }
              checkAlias={validateSiteLeadAlias}
              siteLeadAliasExists={siteLeadAliasExists}
              title={t('ambassadorRegistration.site')}
              description={t('ambassadorRegistration.siteDescription')}
              formValues={formValues}
              setFormValues={setFormValues}
            />
            <RepresentationCheckbox
              checked={formValues.teamRepresentation}
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, teamRepresentation: detail.checked }))
              }
              checkAlias={validateSiteLeadAlias}
              title={t('ambassadorRegistration.team')}
              description={t('ambassadorRegistration.teamDescription')}
              formValues={formValues}
              setFormValues={setFormValues}
            />
            <RepresentationCheckbox
              checked={formValues.employeeLedGroupRepresentation}
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, employeeLedGroupRepresentation: detail.checked }))
              }
              checkAlias={validateSiteLeadAlias}
              title={t('ambassadorRegistration.employeeLedGroup')}
              description={t('ambassadorRegistration.employeeLedGroupDescription')}
              formValues={formValues}
              setFormValues={setFormValues}
            />
            {!isUpdateForm && (
              <>
                <h3>{t('ambassadorRegistration.howDidYouHear')}</h3>
                <FormField stretch>
                  <Select
                    selectedOption={formValues.howDidYouHear}
                    placeholder={t('ambassadorRegistration.selectOption')}
                    data-testid="how-did-you-hear-dropdown"
                    onChange={({ detail }) =>
                      setFormValues((fv) => ({ ...fv, howDidYouHear: detail.selectedOption }))
                    }
                    options={howDidYouHearOptions}
                  />
                </FormField>
                {formValues.howDidYouHear?.label ===
                  t('ambassadorRegistration.otherPleaseSpecify') && (
                  <Textarea
                    onChange={({ detail }) =>
                      setFormValues((fv) => ({ ...fv, howDidYouHearSpecification: detail.value }))
                    }
                    value={formValues.howDidYouHearSpecification}
                    placeholder={t('ambassadorRegistration.pleaseSpecify')}
                    data-testid="ambassador-registration-specify-other-way-to-hear"
                  />
                )}
                <Checkbox
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, myManagerHasApprovedThisRole: detail.checked }))
                  }
                  checked={formValues.myManagerHasApprovedThisRole}
                >
                  {t('ambassadorRegistration.myManagerHasApprovedThisRole')}
                </Checkbox>
              </>
            )}
            <Checkbox
              onChange={({ detail }) =>
                setFormValues((fv) => ({ ...fv, sendMeACopy: detail.checked }))
              }
              checked={formValues.sendMeACopy}
            >
              {t('ambassadorRegistration.sendMeACopy')}
            </Checkbox>
          </SpaceBetween>
        </Container>

        <div>
          <Button
            variant="primary"
            disabled={!isValid}
            loading={loading}
            onClick={submitForm}
            data-testid="ambassador-registration-submit-button"
          >
            {isUpdateForm
              ? t('ambassadorRegistration.save')
              : t('ambassadorRegistration.submitApplication')}
          </Button>
        </div>
      </SpaceBetween>
    </>
  );
}

export const RepresentationCheckbox = ({
  checked,
  title,
  description,
  onChange,
  formValues,
  setFormValues,
  checkAlias,
  siteLeadAliasExists,
}: {
  checked: boolean;
  title: string;
  description: string;
  onChange: NonCancelableEventHandler<CheckboxProps.ChangeDetail> | undefined;
  formValues: AmbassadorRegistrationFormValues;
  setFormValues: Dispatch<SetStateAction<AmbassadorRegistrationFormValues>>;
  checkAlias: () => void;
  siteLeadAliasExists?: boolean;
}) => {
  const { t } = useTranslation(['ambassadors', 'translation']);

  return (
    <FormField stretch>
      <div
        className="ambassador-representation-checkbox"
        data-testid="ambassador-representation-checkbox"
      >
        <SpaceBetween size="s" direction="vertical">
          <Checkbox
            onChange={onChange}
            checked={checked}
            description={description}
            data-testid={`representation-checkbox-open=${checked}`}
          >
            {title}
          </Checkbox>
          {checked && title === t('ambassadorRegistration.site') && (
            <FormField stretch label={t('ambassadorRegistration.siteLead')}>
              <Input
                onChange={({ detail }) =>
                  setFormValues((fv) => ({ ...fv, siteLead: detail.value }))
                }
                invalid={!siteLeadAliasExists}
                onBlur={checkAlias}
                value={formValues.siteLead || ''}
                placeholder={t('ambassadorRegistration.siteLead')}
                data-testid="ambassador-registration-specify-site-lead"
              />
            </FormField>
          )}
          {checked && title === t('ambassadorRegistration.team') && (
            <>
              <FormField stretch label={t('ambassadorRegistration.teamLocation')}>
                <Select
                  selectedOption={formValues.teamLocation || null}
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, teamLocation: detail.selectedOption }))
                  }
                  options={ambassadorRegistrationPossibleCountries}
                  placeholder={t('ambassadorRegistration.selectOption')}
                />
              </FormField>
              <FormField stretch label={t('ambassadorRegistration.teamName')}>
                <Input
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, teamName: detail.value }))
                  }
                  value={formValues.teamName || ''}
                  placeholder={t('ambassadorRegistration.teamName')}
                />
              </FormField>
              <FormField stretch label={t('ambassadorRegistration.teamSize')}>
                <Input
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, teamSize: parseInt(detail.value) }))
                  }
                  value={`${formValues.teamSize}` || ''}
                  inputMode="numeric"
                  type="number"
                  placeholder={t('ambassadorRegistration.teamSize')}
                />
              </FormField>
            </>
          )}
          {checked && title === t('ambassadorRegistration.employeeLedGroup') && (
            <>
              <FormField stretch label={t('ambassadorRegistration.ergName')}>
                <Input
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, ergName: [detail.value] }))
                  }
                  value={formValues.ergName?.[0] || ''}
                  placeholder={t('ambassadorRegistration.ergName')}
                />
              </FormField>
              <FormField stretch label={t('ambassadorRegistration.ergLocation')}>
                <Select
                  selectedOption={formValues.ergLocation || null}
                  onChange={({ detail }) =>
                    setFormValues((fv) => ({ ...fv, ergLocation: detail.selectedOption }))
                  }
                  options={ambassadorRegistrationPossibleCountries}
                  placeholder={t('ambassadorRegistration.selectOption')}
                />
              </FormField>
            </>
          )}
        </SpaceBetween>
      </div>
    </FormField>
  );
};
